<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import crmAPI from '../api/crm';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  deal: {
    type: Object,
    default: null,
  },
  pipeline: {
    type: Object,
    required: true,
  },
  initialStageId: {
    type: [Number, String],
    default: null,
  },
  contact: {
    type: Object,
    default: null,
  },
  conversationId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(['close', 'saved', 'deleted']);

const store = useStore();
const isSaving = ref(false);
const errorMessage = ref('');

const formData = ref({
  id: null,
  title: '',
  value: 0,
  currency: 'BRL',
  pipeline_id: null,
  pipeline_stage_id: null,
  contact_id: null,
  conversation_id: null,
  user_id: null,
  expected_close_date: '',
  lost_reason: '',
});

const agents = computed(() => store.getters['agents/getAgents'] || []);

const stages = computed(() => {
  if (!props.pipeline || !props.pipeline.stages) return [];
  return [...props.pipeline.stages].sort((a, b) => (a.position || 0) - (b.position || 0));
});

const isEditing = computed(() => !!formData.value.id);

const selectedStage = computed(() => {
  return stages.value.find(s => Number(s.id) === Number(formData.value.pipeline_stage_id));
});

const isLostStage = computed(() => {
  return selectedStage.value && selectedStage.value.stage_type === 'lost';
});

const resetForm = () => {
  if (props.deal) {
    formData.value = {
      id: props.deal.id,
      title: props.deal.title || '',
      value: props.deal.value || 0,
      currency: props.deal.currency || 'BRL',
      pipeline_id: props.deal.pipeline_id || props.pipeline.id,
      pipeline_stage_id: props.deal.pipeline_stage_id || (stages.value[0] ? stages.value[0].id : null),
      contact_id: props.deal.contact_id || (props.contact ? props.contact.id : null),
      conversation_id: props.deal.conversation_id || props.conversationId || null,
      user_id: props.deal.user_id || null,
      expected_close_date: props.deal.expected_close_date || '',
      lost_reason: props.deal.lost_reason || '',
    };
  } else {
    formData.value = {
      id: null,
      title: '',
      value: 0,
      currency: 'BRL',
      pipeline_id: props.pipeline.id,
      pipeline_stage_id: props.initialStageId || (stages.value[0] ? stages.value[0].id : null),
      contact_id: props.contact ? props.contact.id : null,
      conversation_id: props.conversationId || null,
      user_id: null,
      expected_close_date: '',
      lost_reason: '',
    };
  }
  errorMessage.value = '';
};

watch(() => props.show, (newVal) => {
  if (newVal) {
    resetForm();
    store.dispatch('agents/get');
  }
});

const handleClose = () => {
  emit('close');
};

const handleSubmit = async () => {
  if (!formData.value.title.trim()) {
    errorMessage.value = 'O título da oportunidade é obrigatório.';
    return;
  }

  isSaving.value = true;
  errorMessage.value = '';

  try {
    const payload = {
      title: formData.value.title.trim(),
      value: Number(formData.value.value) || 0,
      currency: formData.value.currency || 'BRL',
      pipeline_id: formData.value.pipeline_id,
      pipeline_stage_id: formData.value.pipeline_stage_id,
      contact_id: formData.value.contact_id,
      conversation_id: formData.value.conversation_id,
      user_id: formData.value.user_id,
      expected_close_date: formData.value.expected_close_date || null,
      lost_reason: isLostStage.value ? formData.value.lost_reason : null,
    };

    let response;
    if (isEditing.value) {
      response = await crmAPI.updateDeal(formData.value.id, payload);
    } else {
      response = await crmAPI.createDeal(payload);
    }

    emit('saved', response.data);
    handleClose();
  } catch (err) {
    errorMessage.value = err.response?.data?.errors?.join(', ') || 'Erro ao salvar oportunidade.';
  } finally {
    isSaving.value = false;
  }
};

const handleDelete = async () => {
  if (!confirm('Tem certeza que deseja excluir esta oportunidade?')) return;

  isSaving.value = true;
  try {
    await crmAPI.deleteDeal(formData.value.id);
    emit('deleted', formData.value.id);
    handleClose();
  } catch (err) {
    errorMessage.value = 'Erro ao excluir oportunidade.';
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div
    v-if="show"
    class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
    @click.self="handleClose"
  >
    <div class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150">
      <!-- Header -->
      <div class="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
        <h3 class="text-base font-semibold text-slate-900 dark:text-white">
          {{ isEditing ? 'Editar Oportunidade' : 'Nova Oportunidade' }}
        </h3>
        <button
          type="button"
          class="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          @click="handleClose"
        >
          <span class="i-lucide-x w-5 h-5" />
        </button>
      </div>

      <!-- Form Body -->
      <form @submit.prevent="handleSubmit" class="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
        <div v-if="errorMessage" class="p-3 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/60 rounded-lg text-xs text-red-600 dark:text-red-400">
          {{ errorMessage }}
        </div>

        <!-- Title -->
        <div>
          <label class="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
            Título do Negócio *
          </label>
          <input
            v-model="formData.title"
            type="text"
            required
            placeholder="Ex: Contrato Anual, Licenciamento..."
            class="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500"
          />
        </div>

        <!-- Value & Stage in 2 Columns -->
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
              Valor (R$)
            </label>
            <input
              v-model="formData.value"
              type="number"
              step="0.01"
              min="0"
              placeholder="0,00"
              class="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500"
            />
          </div>

          <div>
            <label class="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
              Etapa do Funil *
            </label>
            <select
              v-model="formData.pipeline_stage_id"
              required
              class="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-slate-900 dark:text-white"
            >
              <option v-for="s in stages" :key="s.id" :value="s.id">
                {{ s.name }}
              </option>
            </select>
          </div>
        </div>

        <!-- Assignee & Close Date in 2 Columns -->
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
              Responsável
            </label>
            <select
              v-model="formData.user_id"
              class="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-slate-900 dark:text-white"
            >
              <option :value="null">Sem responsável atribuído</option>
              <option v-for="agent in agents" :key="agent.id" :value="agent.id">
                {{ agent.name }}
              </option>
            </select>
          </div>

          <div>
            <label class="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
              Previsão de Fechamento
            </label>
            <input
              v-model="formData.expected_close_date"
              type="date"
              class="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-slate-900 dark:text-white"
            />
          </div>
        </div>

        <!-- Lost Reason (Conditional) -->
        <div v-if="isLostStage">
          <label class="block text-xs font-medium text-red-600 dark:text-red-400 mb-1">
            Motivo da Perda
          </label>
          <textarea
            v-model="formData.lost_reason"
            rows="2"
            placeholder="Descreva por que a oportunidade foi perdida..."
            class="w-full px-3 py-2 text-sm bg-red-50/50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/60 rounded-lg focus:ring-2 focus:ring-red-500 focus:outline-none text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500"
          />
        </div>

        <!-- Footer Actions -->
        <div class="pt-4 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <div>
            <button
              v-if="isEditing"
              type="button"
              class="px-3 py-2 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/50 rounded-lg transition-colors"
              :disabled="isSaving"
              @click="handleDelete"
            >
              Excluir
            </button>
          </div>

          <div class="flex items-center gap-2">
            <button
              type="button"
              class="px-4 py-2 text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
              :disabled="isSaving"
              @click="handleClose"
            >
              Cancelar
            </button>
            <button
              type="submit"
              class="px-4 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-lg shadow-sm transition-colors flex items-center gap-1.5"
              :disabled="isSaving"
            >
              <span v-if="isSaving" class="i-lucide-loader-2 w-3.5 h-3.5 animate-spin" />
              <span>{{ isEditing ? 'Salvar Alterações' : 'Criar Oportunidade' }}</span>
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>
